// Ad actions (M14): make an ad from a template (filled with the current Photos photo and the brand), swap
// the template keeping the content, put a photo into a slot, apply the brand, save PNG / PDF.
import { create } from 'zustand';
import { useDesign, freeFileName } from '../state';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import { useEditor } from '../../state/editor';
import { useWorkspace } from '../../state/workspace';
import { exportWithExtras } from '../../features/exportHelpers';
import { addAsset } from '../assets';
import { loadBrand, type BrandProfile } from './brand';
import type { BrandData, Doc } from '../model/types';
import { applyBrand, fillSlot, imageNodes, instantiate, templateById } from '../templates/instantiate';
import { FORMATS } from '../templates/dsl';
import { exportPdf, exportPng, type ExportScale } from '../io/exportAd';
import { sanitizeName } from '../../io/rename';

type AdUi = {
  /** The template picker is open (Simple mode shows it instead of the ad). */
  picker: boolean;
  /** The photo "Make an ad" was started from (Photos library id). */
  fromPhoto: string | null;
  busy: string | null;
  message: string | null;
  brandOpen: boolean;
};
export const useAdUi = create<AdUi>(() => ({
  picker: false,
  fromPhoto: null,
  busy: null,
  message: null,
  brandOpen: false,
}));

/** "Make an ad" from Photos: switch to Design and open the template picker for the active photo. */
export function makeAdFromPhotos() {
  useAdUi.setState({ picker: true, fromPhoto: useLibrary.getState().activeId, message: null });
  useWorkspace.getState().setWs('design');
}

/** The brand as stored in a document (the logo becomes an embedded asset). */
export async function brandData(p: BrandProfile): Promise<BrandData> {
  const { logo, ...rest } = p;
  return {
    ...rest,
    logoAsset: logo ? await addAsset(logo.bytes, logo.type) : null,
    logoW: logo?.w ?? 0,
    logoH: logo?.h ?? 0,
  };
}

const photoKey = (p: LibraryPhoto) => `${p.name}|${p.size}|${p.lastModified}`;

/**
 * Renders a Photos photo for a slot: with its current edits, cutout and upscale, through the export worker,
 * at the pixel size the slot needs at the largest export size (2× screen, 300 dpi print), capped at 3200 px.
 * A transparent cutout stays a PNG; anything else is a JPEG q90.
 */
export async function renderPhotoForSlot(p: LibraryPhoto, slot: { w: number; h: number }, print: boolean) {
  const s = useEditor.getState().get(p.id);
  const cutout = s.background.mode !== 'off' && s.background.fill.kind === 'transparent';
  const edge = Math.min(3200, Math.round(Math.max(slot.w, slot.h) * (print ? 300 / 72 : 2) * 1.25));
  const res = await exportWithExtras(
    p,
    s,
    {
      format: cutout ? 'png' : 'jpeg',
      bitDepth: 8,
      quality: 90,
      size: { mode: 'longEdge', w: edge },
      dpi: 72,
      colorSpace: 'srgb',
      sharpen: { target: 'screen', amount: 'standard' },
      background: 'asEdited',
      metadata: 'stripAll',
    },
    { artist: '', copyright: '', email: '', website: '' },
  );
  return { bytes: res.bytes, type: res.mime, w: res.width, h: res.height };
}

/** Puts a Photos photo into an image slot of the open ad (one undo step). */
export async function fillPhotoSlot(slotId: string, photoId: string) {
  const doc = useDesign.getState().doc;
  const p = useLibrary.getState().photos.find((x) => x.id === photoId);
  const slot = doc && imageNodes(doc).find((n) => n.id === slotId);
  if (!doc || !p || !slot) return;
  useAdUi.setState({ busy: 'photo' });
  try {
    const r = await renderPhotoForSlot(p, slot.frame, doc.rasterDpi >= 150);
    const id = await addAsset(r.bytes, r.type);
    useDesign.getState().run(fillSlot(slotId, { id, w: r.w, h: r.h }, photoKey(p)));
    useDesign.getState().endGesture();
  } finally {
    useAdUi.setState({ busy: null });
  }
}

/** Puts an image file (not from Photos) into a slot. */
export async function fillSlotFromFile(slotId: string, file: Blob) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const bmp = await createImageBitmap(new Blob([bytes]));
  const id = await addAsset(bytes, file.type || 'image/jpeg');
  useDesign.getState().run(fillSlot(slotId, { id, w: bmp.width, h: bmp.height }));
  useDesign.getState().endGesture();
  bmp.close();
}

/** Creates the ad document for a template; fills the photo slots with `photoId` when given. */
export async function createAd(templateId: string, photoId: string | null, keep?: Doc | null): Promise<void> {
  const t = templateById(templateId);
  if (!t) return;
  useAdUi.setState({ busy: 'template', message: null });
  try {
    const brand = await brandData(await loadBrand());
    const doc = instantiate(t, brand, keep?.ad);
    // A template swap keeps the photo already in the ad.
    const kept = keep && imageNodes(keep, 'photo').find((n) => n.assetId);
    if (kept)
      for (const n of imageNodes(doc, 'photo'))
        Object.assign(n, { assetId: kept.assetId, iw: kept.iw, ih: kept.ih, photoKey: kept.photoKey });
    const name = `${t.name.es} — ${new Date().toISOString().slice(0, 10)}`;
    doc.name = name;
    useDesign.getState().createFrom(doc, await freeFileName(name));
    useAdUi.setState({ picker: false });
    if (photoId && !kept) {
      useAdUi.setState({ busy: 'photo' });
      for (const slot of imageNodes(doc, 'photo')) await fillPhotoSlot(slot.id, photoId);
    }
  } finally {
    useAdUi.setState({ busy: null, fromPhoto: null });
  }
}

/** Re-applies "My brand" to the open ad (after it changed). */
export async function applyBrandToAd() {
  const doc = useDesign.getState().doc;
  if (!doc?.template) return;
  useDesign.getState().run(applyBrand(await brandData(await loadBrand())));
  useDesign.getState().endGesture();
}

function download(bytes: Uint8Array, name: string, type: string) {
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Saves the open ad: into the folder's exports/ads/ when a folder is open, else a download. */
export async function saveAd(kind: 'png' | 'pdf', scale?: ExportScale): Promise<string> {
  const doc = useDesign.getState().doc;
  if (!doc) throw new Error('no ad open');
  const base = sanitizeName(doc.name) || 'ad';
  const bytes = kind === 'png' ? await exportPng(doc, scale!) : await exportPdf(doc);
  const name = kind === 'png' ? `${base}_${scale!.id}.png` : `${base}.pdf`;
  const folder = useLibrary.getState().folder;
  if (folder?.mode === 'fsa') return folder.writeExport('ads', name, bytes);
  download(bytes, name, kind === 'png' ? 'image/png' : 'application/pdf');
  return name;
}

export const formatOf = (doc: Doc | null) =>
  doc?.template ? FORMATS[templateById(doc.template)?.format ?? 'ig-square'] : null;
